import React from 'react';
import { X, User as UserIcon, Trophy, Zap, Star, Crown, Diamond } from 'lucide-react';
import { User } from '../types';
import { getUserRank, determineUserTier } from '../utils/rankUtils';

interface UserInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User | null;
    isDarkMode: boolean;
    config: any;
}

const UserInfoModal: React.FC<UserInfoModalProps> = ({
    isOpen,
    onClose,
    user,
    isDarkMode,
    config
}) => {
    if (!isOpen || !user) return null;

    const userTier = determineUserTier(user);
    const rankConfig = getUserRank(user, config);
    const isMax = userTier === 'max';
    const isPremium = userTier === 'premium';

    // Get gamification data
    const level = user.level || 1;
    const xp = user.xp || 0;
    const nextLevelXP = (level + 1) * 1000; // Simple calculation
    const xpProgress = (xp / nextLevelXP) * 100;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
            <div 
                onClick={(e) => e.stopPropagation()}
                className={`w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-300 ${
                    isDarkMode ? 'bg-slate-900' : 'bg-white'
                }`}
            >
                {/* Header */}
                <div className="relative p-6 border-b overflow-hidden" style={{
                    background: isMax 
                        ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                        : isPremium
                            ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                            : 'linear-gradient(135deg, #64748b, #475569)'
                }}>
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl opacity-20 -mr-16 -mt-16"></div>
                    
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {/* Avatar */}
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                                isMax
                                    ? 'bg-white/20 text-white'
                                    : isPremium
                                        ? 'bg-white/20 text-white'
                                        : 'bg-white/20 text-white'
                            }`}>
                                {isMax ? (
                                    <Diamond size={32} />
                                ) : isPremium ? (
                                    <Crown size={32} />
                                ) : (
                                    <span>{user.username?.[0]?.toUpperCase() || 'U'}</span>
                                )}
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">{user.username || 'User'}</h2>
                                <p className="text-sm text-white/80">{rankConfig?.name || (isMax ? 'MAX' : isPremium ? 'Premium' : 'Free')}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className={`p-6 space-y-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {/* User ID */}
                    <div className={`p-4 rounded-xl border ${
                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-1">
                            <UserIcon size={16} className={isDarkMode ? 'text-slate-400' : 'text-slate-600'} />
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                User ID
                            </span>
                        </div>
                        <p className="font-mono text-sm">{user.id}</p>
                    </div>

                    {/* Username & Rank */}
                    <div className={`p-4 rounded-xl border ${
                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-2">
                            <Trophy size={16} className="text-amber-500" />
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                Username & Rank
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="font-bold">{user.username || 'Guest'}</p>
                            <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                                isMax
                                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                    : isPremium
                                        ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white'
                                        : isDarkMode
                                            ? 'bg-slate-700 text-slate-300'
                                            : 'bg-slate-200 text-slate-700'
                            }`}>
                                {rankConfig?.name || (isMax ? 'MAX TIER' : isPremium ? 'PREMIUM' : 'FREE')}
                            </div>
                        </div>
                    </div>

                    {/* Gamification Level */}
                    <div className={`p-4 rounded-xl border ${
                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-3">
                            <Zap size={16} className="text-indigo-500" />
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                Gamification Level
                            </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-2xl font-bold text-indigo-500">Level {level}</span>
                            <span className="text-sm text-slate-500">{xp.toLocaleString()} / {nextLevelXP.toLocaleString()} XP</span>
                        </div>
                        {/* XP Progress Bar */}
                        <div className={`w-full h-2 rounded-full overflow-hidden ${
                            isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                        }`}>
                            <div 
                                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                                style={{ width: `${Math.min(xpProgress, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* Status/Achievements */}
                    <div className={`p-4 rounded-xl border ${
                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-3">
                            <Star size={16} className="text-yellow-500" />
                            <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                Status
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-blue-500">{user.questsCompleted || 0}</p>
                                <p className="text-xs text-slate-500">Quests</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-500">{user.achievements?.length || 0}</p>
                                <p className="text-xs text-slate-500">Badges</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-purple-500">{user.streakCount || 0}</p>
                                <p className="text-xs text-slate-500">Streak</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={`p-4 border-t ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                    <button
                        onClick={onClose}
                        className={`w-full py-2 rounded-lg font-medium transition-colors ${
                            isDarkMode
                                ? 'bg-slate-800 hover:bg-slate-700 text-white'
                                : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                        }`}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserInfoModal;
